import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import AddCircle from '@mui/icons-material/AddCircle';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import CircularProgress from '@mui/material/CircularProgress';

import { createSearchParams, useSearchParams, useNavigate } from 'react-router-dom';
import { objectToQueryString, baseUrl, apiHelper } from 'helpers/util';

import ColumnBox from 'components/ColumnBox';
import SmallHeading from 'components/SmallHeading';

export default function Form(props) {
    const navigate = useNavigate();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ sessionName, setSessionName ] = useState(null);
    const [ sessionDate, setSessionDate ] = useState(null);

    const [ attendees, setAttendees ] = useState([]);
    const [ attendeeOptions, setAttendeeOptions ] = useState([]);
    const [ attendeeLabelHash, setAttendeeLabelHash ] = useState({});

    const [ topicsCovered, setTopicsCovered ] = useState([]);
    const [ topicOptions, setTopicOptions ] = useState([]);

    const [ takenBy, setTakenBy ] = useState(null);
    const [ remarks, setRemarks ] = useState('');
    const [ isFormSubmitting, setIsFormSubmitting ] = useState(false);

    const onClickSave = function() {
        const attendeesPayload = [];
        for (const a of attendees) {
            attendeesPayload.push({ "attendee": a })
        }

        const topicsPayload = [];
        for (const t of topicsCovered) {
            topicsPayload.push({ "topic": t })
        }

        const payload = { 'session_date': sessionDate && sessionDate.toFormat ?
                sessionDate.toFormat('yyyy-MM-dd') : sessionDate,
            remarks, 'taken_by': takenBy, "session_attendees": attendeesPayload,
            "topics_covered": topicsPayload }

        const apiParams = { "doctype": "FOLK Training Session" };
        sessionName && (apiParams["name"] = sessionName)

        setIsFormSubmitting(true);
        let apiUrl = `${baseUrl}/.netlify/functions/erp_api`;
        let apiPromise = null;
        if (sessionName) {
            apiPromise = apiHelper.put({ "url": apiUrl, "params": apiParams, "data": payload })
        } else {
            apiPromise = apiHelper.post({ "url": apiUrl, "params": apiParams, "data": payload })
        }
        apiPromise
        .then(response => {
            if (response.status) {
                navigate({ "pathname": "/training_index" });
                setIsFormSubmitting(false);
            } else { console.log(response.json); setIsFormSubmitting(false); }
        })
    }

    const loadAttendeeOptions = function() {
        const apiParams = {
            "doctype": "User",
            "fields": JSON.stringify(["name", "full_name"]),
            "order_by": "full_name asc",
            "limit": 1000,
            "filters": JSON.stringify([["enabled", "=", 1]])
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) {
                const attendeeLabelHash = {};
                setAttendeeOptions(response.json.data.map(el => el.name));
                for (const r of response.json.data) { attendeeLabelHash[r.name] = r.full_name; }
                setAttendeeLabelHash(attendeeLabelHash);
            } else { console.log(response.json); }
        })
    }

    const loadTopicOptions = function() {
        const apiParams = {
            "doctype": "FOLK Training Topic",
            "fields": JSON.stringify(["name"]),
            "order_by": "name asc",
            "limit": 1000
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) { setTopicOptions(response.json.data.map(el => el.name)); }
            else { console.log(response.json); }
        })
    }

    const loadSessionDetail = function(trainingName) {
        const apiParams = {
            "doctype": "FOLK Training Session",
            "name": trainingName,
            "fields": JSON.stringify(["*"]),
            "limit": "100"
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) {
                fillData(response.json.data);
            } else {
                console.log(response.json);
            }
        })
    }

    const fillData = function(data) {
        setSessionName(data ? data.name : null);

        setSessionDate(data ? data.session_date : null);
        setTakenBy(data ? data.taken_by : null);
        setTopicsCovered(data ? data.topics_covered.map(el => el.topic) : []);
        setAttendees(data ? data.session_attendees.map(el => el.attendee) : []);
        setRemarks(data ? data.remarks : '');
    }

    useEffect(function() {
        loadAttendeeOptions();
        loadTopicOptions();
        if (searchParams.get('name')) {
            loadSessionDetail(searchParams.get('name'));
        }
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center',
            padding: '15px' }}>
            <ColumnBox sx={{ alignItems: 'center', maxWidth: '500px',
                width: '100%' }}>
                <SmallHeading>Session Details</SmallHeading>
                <Box sx={{ marginTop: '15px', width: '100%' }}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <MobileDatePicker
                            onChange={(newValue) => { setSessionDate(newValue); }}
                            value={sessionDate}
                            inputFormat="dd-MMM-yyyy"
                            mask=""
                            closeOnSelect={true}
                            disableMaskedInput={true}
                            renderInput={(params) => <TextField {...params}
                                label="Session Date" size="small" sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>
                <Autocomplete
                    disablePortal
                    size="small"
                    onChange={(e, newValue) => { setTakenBy(newValue); }}
                    options={attendeeOptions}
                    value={takenBy}
                    sx={{ width: '100%', marginTop: '15px' }}
                    getOptionLabel={el => attendeeLabelHash[el] || el}
                    renderInput={(params) => <TextField {...params} label="Taken By" />}
                />
                <Autocomplete
                    disablePortal
                    size="small"
                    multiple={true}
                    onChange={(e, newValue) => { setTopicsCovered(newValue); }}
                    options={topicOptions}
                    disableCloseOnSelect={true}
                    value={topicsCovered}
                    sx={{ width: '100%', marginTop: '15px' }}
                    renderInput={(params) => <TextField {...params} label="Choose Topics" />}
                />
                <Autocomplete
                    disablePortal
                    size="small"
                    multiple={true}
                    onChange={(e, newValue) => { setAttendees(newValue); }}
                    options={attendeeOptions}
                    disableCloseOnSelect={true}
                    value={attendees}
                    sx={{ width: '100%', marginTop: '15px' }}
                    getOptionLabel={el => attendeeLabelHash[el] || el}
                    renderInput={(params) => <TextField {...params} label="Choose Attendees" />}
                />
                <TextField label="Remarks"
                    sx={{ width: '100%', marginTop: '15px' }}
                    minRows={2}
                    onChange={(e) => { setRemarks(e.target.value); }}
                    value={remarks}
                    multiline={true} />

                { isFormSubmitting ? <CircularProgress sx={{ marginTop: '15px' }} /> :
                    <Button sx={{ marginTop: '15px' }} onClick={() => onClickSave()}
                        variant="outlined">Save</Button>
                }
            </ColumnBox>
        </Box>
    );
}
